/**
 * `/login` — the form, plus the decision about where signing in leads.
 *
 * That split is deliberate: the form is testable on its own because it does not
 * know what a route is, and this page is where the routing lives.
 */

import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { useSession } from "../../auth/AuthProvider";
import { AuthCard } from "./AuthCard";
import { LoginForm } from "./LoginForm";

/** Set by `RequireAuth` when it turned someone away, and by the reset-password
 * page when it wants a confirmation shown. */
interface LoginState {
  from?: string;
  notice?: string;
}

export function Login() {
  const { api, user, checking, refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LoginState;

  // Somewhere they were sent from, or the dashboard.
  const destination = state.from ?? "/dashboard";

  // Already signed in — usually a bookmarked `/login` or the Back button after
  // logging in. Showing the form would invite them to do it again.
  if (!checking && user) return <Navigate to={destination} replace />;

  return (
    <AuthCard
      title="Sign in"
      footer={
        <>
          No account yet? <Link to="/register">Create one</Link>.
          <span className="bear-line">
            Big Bear doesn&rsquo;t know your passphrase. That&rsquo;s the point.
          </span>
        </>
      }
    >
      <LoginForm
        api={api}
        {...(state.notice ? { notice: state.notice } : {})}
        onSignedIn={async () => {
          // The session is re-read before navigating, so the gate on the
          // destination sees a signed-in user rather than bouncing straight
          // back here.
          await refresh();
          navigate(destination, { replace: true });
        }}
      />
    </AuthCard>
  );
}
